/**
 * Reduction Target Card Component
 * 
 * Displays the organisation's emission reduction target progress.
 * Extracted from the carousel for permanent placement on Dashboard.
 */

import { Button } from '@/components/ui/button';
import { Thermometer, Settings2 } from 'lucide-react';

export default function ReductionTargetCard({ 
  reductionTarget,
  onSetTarget,
  onConfigure,
  className = ''
}) {
  const hasTarget = !!reductionTarget?.baseline_year;

  return (
    <div className={`bg-white rounded-2xl p-5 shadow-sm border border-slate-100 ${className}`} data-testid="reduction-target-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-[#003B5C]/10 rounded-xl flex items-center justify-center">
            <Thermometer className="w-5 h-5 text-[#003B5C]" />
          </div>
          <div>
            <h3 className="font-semibold text-[#003B5C]">Reduction Target Progress</h3>
            <p className="text-xs text-slate-500">Track your journey to net zero</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {hasTarget && onConfigure && (
            <Button
              onClick={onConfigure}
              variant="ghost"
              size="sm"
              className="text-slate-500 hover:text-[#003B5C]"
              title="Configure target settings"
              data-testid="configure-target-btn"
            >
              <Settings2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            onClick={onSetTarget}
            variant="outline"
            size="sm"
            className="text-[#74A57F] border-[#74A57F] hover:bg-[#74A57F] hover:text-white"
            data-testid="set-target-btn"
          >
            {hasTarget ? 'Update' : 'Set Target'}
          </Button>
        </div>
      </div>
      
      {hasTarget ? (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500 mb-1">Baseline FY{reductionTarget.baseline_year}</p>
            <p className="text-xl font-bold text-[#003B5C]">{reductionTarget.baseline_emissions?.toFixed(1)}</p>
            <p className="text-xs text-slate-500">tCO₂-e</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500 mb-1">Target FY{reductionTarget.target_year}</p>
            <p className="text-xl font-bold text-[#74A57F]">{reductionTarget.target_emissions?.toFixed(1)}</p>
            <p className="text-xs text-slate-500">-{reductionTarget.reduction_target_percent}%</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-center">
            <p className="text-xs text-slate-500 mb-1">Net FY{reductionTarget.current_year}</p>
            <p className="text-xl font-bold text-[#003B5C]">{reductionTarget.current_emissions?.toFixed(1)}</p>
            {reductionTarget.offsets_applied > 0 && (
              <p className="text-[10px] text-emerald-600">-{reductionTarget.offsets_applied?.toFixed(1)} offsets</p>
            )}
          </div>
          <div className={`rounded-lg p-3 text-center ${reductionTarget.on_track ? 'bg-emerald-50' : 'bg-amber-50'}`}>
            <p className="text-xs text-slate-500 mb-1">Progress</p>
            <p className={`text-xl font-bold ${reductionTarget.on_track ? 'text-emerald-600' : 'text-amber-600'}`}>
              {reductionTarget.progress_percent?.toFixed(0)}%
            </p>
            <p className={`text-xs ${reductionTarget.on_track ? 'text-emerald-600' : 'text-amber-600'}`}>
              {reductionTarget.on_track ? '✓ On Track' : '⚠ Behind'}
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 bg-slate-50 rounded-xl">
          <Thermometer className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No reduction target set</p>
          <p className="text-xs text-slate-400 mt-1">Set a target to track your progress toward net zero</p>
        </div>
      )}
    </div>
  );
}
